import {
  Component,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Renderer2,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core'
import { CookieService } from 'ngx-cookie-service'
import { Observable, Subscription } from 'rxjs'
import { PortfolioHoldingsSortPipe } from 'src/app/features/shared/pipes/portfolio-holdings-sort.pipe'
import { PcfBasketResponse } from 'src/app/models/portfolio-models/pcf-basket-response.model'
import {
  DateRangeHoldingResponse,
  PortfolioHoldingResponse,
} from 'src/app/models/portfolio-models/portfolio-holding-response.model'
import { AEMChunk } from 'src/app/services/AEM/aem-chunk.model'
import { CentralContentService } from 'src/app/services/content/central-content.service'
import { environment } from 'src/environments/environment'

import { ProfileData } from '../../../portfolio.model'
import { PortfolioService } from '../../../portfolio.service'
import { SEC_LINK_REPOSITORY } from '../../sec/sec-link-repository'

const maxLength = 10
const CUSIP_LEGAL_COOKIE = 'ps_agreedToCUSIPTerms'
const MOBILE_THRESHOLD = 576
const TABLET_THRESHOLD = 768
const PCF_HOLDINGS = 'pcf-holdings'
const SHORT_TERM_RESERVE = 'short-term reserve'

@Component({
  selector: 'holding-details-container',
  templateUrl: './holding-details-container.component.html',
  styleUrls: ['./holding-details-container.component.scss'],
  providers: [],
})
export class HoldingDetailsContainerComponent
  implements OnChanges, OnInit, OnDestroy
{
  @ViewChild('simpleDialogContent') simpleDialogContent: TemplateRef<any>
  @ViewChild('legalTermsDialog') legalTermsDialog: TemplateRef<any>
  @Input() asset: string
  @Input() holdingsStock: PortfolioHoldingResponse
  @Input() holdingsBond: PortfolioHoldingResponse
  @Input() holdingsShortTerm: PortfolioHoldingResponse
  @Input() holdingsDerivative: PortfolioHoldingResponse
  @Input() holdingsCurrency: PortfolioHoldingResponse
  @Input() holdingsCommodity: PortfolioHoldingResponse
  @Input() holdingsStockDaily: PortfolioHoldingResponse
  @Input() holdingsBondDaily: PortfolioHoldingResponse
  @Input() holdingsShortTermDaily: PortfolioHoldingResponse
  @Input() holdingsDerivativeDaily: PortfolioHoldingResponse
  @Input() holdingsMoneyMarket: PortfolioHoldingResponse
  @Input() holdingsDateRange: DateRangeHoldingResponse

  moneyMarketDateRanged: PortfolioHoldingResponse
  pcfHoldingFormatted: PortfolioHoldingResponse
  incomingExtraHoldings: PortfolioHoldingResponse = {}
  pcfBasket$: Observable<PcfBasketResponse>
  pcfAcceptedFlag = false
  pcfAsOfDate: string
  @Input() profileData: ProfileData

  mainDataSet: PortfolioHoldingResponse
  currentType = ''
  asOfDate: string
  viewDaily = false
  viewPreviousQuarter = false
  legalAccepted = false
  sortBy = 'marketValue'
  defaultDirection = 'desc'
  secLink: string
  public activeTab: string
  public pageNumber = 1
  public listSize: number
  isMobileDevice: boolean
  isTabletDevice: boolean
  swapFileUrl = environment.swapFileUrl
  windowWidth = 0
  noData = true
  advisorsStategyFund = environment.advisorsStategyFund

  //AEM content variables
  holdingsDetailsDisclaimer: AEMChunk
  moneyMarketCusipDisclaimer: AEMChunk
  institutionalInvestorDisclaimer: AEMChunk
  aemContentSub: Subscription

  // eslint-disable-next-line complexity, max-params,
  constructor(
    private readonly viewContainer: ViewContainerRef,
    private readonly portfolioHoldingsSort: PortfolioHoldingsSortPipe,
    private readonly cookieService: CookieService,
    private readonly portfolioService: PortfolioService,
    private readonly renderer: Renderer2,
    private readonly centralContentService: CentralContentService,
  ) {}
  ngOnInit(): void {
    this.onResize()
    this.determineViewDaily()
    this.determineInitialSet()
    this.aemContentSub = this.centralContentService.aemContent$.subscribe(
      (content) => {
        this.parseAEMContent(content)
      },
    )
  }

  parseAEMContent(content) {
    this.holdingsDetailsDisclaimer =
      this.centralContentService.getContentByBody(
        'disclaimer_holding_details',
        content,
      )
    this.moneyMarketCusipDisclaimer =
      this.centralContentService.getContentByBody(
        'disclaimer_money_market_cusip_terms_and_conditions',
        content,
      )
    this.institutionalInvestorDisclaimer =
      this.centralContentService.getContentByBody(
        'disclaimer_portfolio_composition_institutional_investors',
        content,
      )
  }

  @HostListener('window:resize')
  onResize() {
    this.windowWidth = window.innerWidth
    this.isMobileDevice = window.innerWidth <= MOBILE_THRESHOLD
    this.isTabletDevice = window.innerWidth <= TABLET_THRESHOLD
  }

  // eslint-disable-next-line complexity
  ngOnChanges(changes: SimpleChanges) {
    if (!changes.holdingsStock?.firstChange) {
      this.onResize()
      this.determineViewDaily()
      this.determineInitialSet()
      if (this.profileData?.isMoneyMarket) {
        this.legalAccepted =
          this.cookieService.get(CUSIP_LEGAL_COOKIE) === 'true'
        this.secLink = this.parseLink()
      }
    }
  }
  determineViewDaily() {
    if (
      (!this.profileData?.isIndex && this.profileData?.isETF) ||
      this.profileData?.isStandaloneEtf
    ) {
      this.viewDaily = true
      this.viewPreviousQuarter = false
    }
  }
  parseLink(): string {
    return environment.investorPrefix
      .concat('external-site-message?url=')
      .concat(encodeURIComponent(SEC_LINK_REPOSITORY[this.profileData?.fundId]))
  }
  // eslint-disable-next-line complexity, max-statements
  determineInitialSet() {
    if (this.currentType === '') {
      this.checkNoData()
      if (this.noData) {
        return
      }
      if (this.profileData?.isMoneyMarket) {
        this.setData(SHORT_TERM_RESERVE)
        this.activeTab = 'short-term reserve'
      } else if (this.profileData?.isEtfOfEtfs) {
        this.getPcfHoldings()
        this.activeTab = 'pcf-holdings'
      } else if (
        this.profileData?.isBond ||
        this.profileData?.fundId === '0517'
      ) {
        this.setData('bond')
        this.activeTab = 'bond'
      } else if (this.profileData?.fundId === '1298') {
        this.setData('commodity')
        this.activeTab = 'commodity'
      } else {
        this.setData('equity')
        this.activeTab = 'equity'
      }
    }
    this.listSize = this.mainDataSet?.size
  }
  setPageNumber(changedNumber: number) {
    this.pageNumber = changedNumber
    this.setData(this.currentType)
  }
  checkNoData() {
    let allHoldings = []
    if (this.viewDaily) {
      allHoldings = [
        this.holdingsBondDaily,
        this.holdingsStockDaily,
        this.holdingsShortTermDaily,
        this.holdingsDerivativeDaily,
      ]
    } else {
      allHoldings = [
        this.holdingsBond,
        this.holdingsCurrency,
        this.holdingsDerivative,
        this.holdingsMoneyMarket,
        this.holdingsShortTerm,
        this.holdingsStock,
      ]
    }
    for (const value of allHoldings) {
      if (value?.size > 0) {
        this.noData = false
        break
      }
    }
  }
  // eslint-disable-next-line complexity
  setData(type: string) {
    this.currentType = type
    this.sortBy = 'marketValue'
    switch (true) {
      case type === 'equity':
        this.mainDataSet = this.sliceData(
          this.sortData(
            this.viewDaily ? this.holdingsStockDaily : this.holdingsStock,
          ),
        )
        break
      case type === 'bond':
        this.mainDataSet = this.sliceData(
          this.sortData(
            this.viewDaily ? this.holdingsBondDaily : this.holdingsBond,
          ),
        )
        break
      case type === SHORT_TERM_RESERVE:
        this.sortBy = 'principalAmount'
        let tempToSort
        if (this.profileData.isMoneyMarket) {
          tempToSort = this.holdingsMoneyMarket
        } else if (this.viewDaily) {
          tempToSort = this.holdingsShortTermDaily
        } else {
          tempToSort = this.holdingsShortTerm
        }
        this.mainDataSet = this.sliceData(this.sortData(tempToSort))
        break
      case type === 'money-market-range':
        this.sortBy = 'principalAmount'
        this.mainDataSet = this.sliceData(
          this.sortData(this.moneyMarketDateRanged),
        )
        break
      case type === 'derivative':
        this.mainDataSet = this.sliceData(
          this.sortData(
            this.viewDaily
              ? this.holdingsDerivativeDaily
              : this.holdingsDerivative,
          ),
        )
        break
      case type === 'currency':
        this.mainDataSet = this.sliceData(this.sortData(this.holdingsCurrency))
        break
      case type === 'commodity':
        this.mainDataSet = this.sliceData(this.sortData(this.holdingsCommodity))
        break
      case type === PCF_HOLDINGS:
        this.sortBy = 'sharesHeld'
        this.mainDataSet = this.sliceData(
          this.sortData(this.pcfHoldingFormatted),
        )
    }
    this.listSize = this.mainDataSet?.size
  }
  sortData(incomingData: PortfolioHoldingResponse): PortfolioHoldingResponse {
    if (!incomingData?.fund?.entity) {
      return incomingData
    }
    incomingData.fund.entity = this.portfolioHoldingsSort.transform(
      incomingData?.fund?.entity,
      this.sortBy,
      this.defaultDirection,
    )
    return incomingData
  }
  sliceData(dataSet: PortfolioHoldingResponse): PortfolioHoldingResponse {
    if (!dataSet) {
      return dataSet
    }
    const temp = JSON.parse(JSON.stringify(dataSet))

    if (temp.size === 0) {
      temp.fund.entity = [{}]
      return temp
    }
    if (temp.size > maxLength) {
      temp.fund.entity = temp.fund.entity.slice(
        (this.pageNumber - 1) * maxLength,
        this.pageNumber * maxLength,
      )
    }
    return temp
  }

  fetchDateRangeResults(dateRangedMoneyMarket: PortfolioHoldingResponse) {
    this.currentType = 'money-market-range'
    this.moneyMarketDateRanged = dateRangedMoneyMarket

    this.mainDataSet = this.sliceData(this.sortData(dateRangedMoneyMarket))

    this.listSize = this.mainDataSet?.size
  }
  userLegalAction(isAccepted: boolean) {
    //closeDialog
    this.legalAccepted = isAccepted
    if (isAccepted) {
      this.cookieService.set(CUSIP_LEGAL_COOKIE, 'true')
    }
  }
  getPcfHoldings() {
    if (!this.isMobileDevice) {
      this.currentType = Object.assign([], PCF_HOLDINGS)
    }
    if (!this.pcfBasket$) {
      this.pcfBasket$ = this.portfolioService.getPcfHoldingData(
        this.profileData?.fundId,
      )
    }
    this.sortBy = 'sharesHeld'
    this.extractPcfHoldings(this.pcfBasket$)
  }
  setMobileData(event: string) {
    if (event === PCF_HOLDINGS) {
      this.getPcfHoldings()
    } else {
      this.setData(event)
    }
  }
  extractPcfHoldings(pcfObs$: Observable<PcfBasketResponse>) {
    pcfObs$.subscribe((pcfBasketHoldings) => {
      console.log(pcfBasketHoldings)
      this.currentType = PCF_HOLDINGS
      const pcfDate = this.reconstructDate(pcfBasketHoldings?.trdDate)
      this.pcfAsOfDate = pcfDate

      this.processPcfHoldingResults({
        size: pcfBasketHoldings?.holding?.length,
        asOfDate: pcfDate,
        fund: { entity: pcfBasketHoldings?.holding },
      })
    })
  }
  reconstructDate(incomingDate: string) {
    if (!incomingDate) {
      return ''
    }
    const dateSplit = incomingDate.split('/')
    /* eslint-disable @typescript-eslint/no-magic-numbers */ // <-- Allow long lines starting from here
    const year = dateSplit[2].split(' ')[0]
    return year
      .concat('-')
      .concat(dateSplit[0])
      .concat('-')
      .concat(dateSplit[1])
      .concat('-')
      .concat('T00:00:00-04:00')
    /* eslint-enable @typescript-eslint/no-magic-numbers */ // <-- Remove ling line rules exception here
  }
  processPcfHoldingResults(pcfHoldings: PortfolioHoldingResponse) {
    this.pcfHoldingFormatted = pcfHoldings
    this.mainDataSet = this.sliceData(this.sortData(pcfHoldings))
    this.listSize = this.mainDataSet?.size
  }

  setPcfFlag(accepted: true) {
    this.pcfAcceptedFlag = accepted
  }
  modifyHoldings(
    incomingExtraHoldingsAndPage: Record<
      string,
      number | PortfolioHoldingResponse
    >,
  ) {
    const incomingExtraHoldings =
      incomingExtraHoldingsAndPage.holdings as PortfolioHoldingResponse
    const incomingPage = incomingExtraHoldingsAndPage.page as number

    this.setHoldingsBasedOnType(incomingExtraHoldings)
    this.setPageNumber(incomingPage)
  }
  // eslint-disable-next-line complexity
  setHoldingsBasedOnType(incoming: PortfolioHoldingResponse) {
    if (this.viewDaily) {
      switch (this.currentType) {
        case 'equity':
          this.holdingsStockDaily = incoming
          break
        case 'bond':
          this.holdingsBondDaily = incoming
          break
        case SHORT_TERM_RESERVE:
          this.holdingsShortTermDaily = incoming
          break
        case 'derivative':
          this.holdingsDerivativeDaily = incoming
          break
      }
    } else {
      switch (this.currentType) {
        case 'equity':
          this.holdingsStock = incoming
          break
        case 'bond':
          this.holdingsBond = incoming
          break
        case SHORT_TERM_RESERVE:
          if (this.profileData?.isMoneyMarket) {
            this.holdingsMoneyMarket = incoming
          } else {
            this.holdingsShortTerm = incoming
          }
        case 'derivative':
          this.holdingsDerivative = incoming
          break
        case 'currency':
          this.holdingsCurrency = incoming
          break
        case 'commodity':
          this.holdingsCommodity = incoming
          break
        case PCF_HOLDINGS:
          this.pcfHoldingFormatted = incoming
          break
      }
    }
  }
  ngOnDestroy(): void {
    this.aemContentSub.unsubscribe()
  }
}
